import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { ROOT_URL } from "../../api/ApiConfig";

function authHeaders(getState) {
  const token = getState().loginToken?.response?.token;
  return {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };
}

function ticketFromEmailFromPayload(payload) {
  if (payload == null || typeof payload !== "object") return "";
  const raw =
    payload?.data?.ticket_from_email ??
    payload?.data?.data?.ticket_from_email ??
    payload?.ticket_from_email ??
    payload?.result?.ticket_from_email ??
    payload?.data?.email ??
    payload?.email;
  if (raw == null) return "";
  return String(raw).trim();
}

export const fetchGroupTicketFromEmail = createAsyncThunk(
  "groupTicketFromEmail/fetch",
  async ({ groupId }, { getState, rejectWithValue }) => {
    if (!groupId) {
      return rejectWithValue({ message: "Missing group id" });
    }
    try {
      const response = await axios.get(
        `${ROOT_URL}/group/${groupId}/ticket-from-email`,
        authHeaders(getState),
      );
      return { groupId, payload: response.data };
    } catch (error) {
      if (error.response?.status === 404) {
        return rejectWithValue({ groupId, notFound: true, status: 404 });
      }
      return rejectWithValue(
        error.response?.data || { message: error.message, groupId },
      );
    }
  },
);

export const patchGroupTicketFromEmail = createAsyncThunk(
  "groupTicketFromEmail/patch",
  async ({ groupId, ticket_from_email }, { getState, rejectWithValue }) => {
    if (!groupId) {
      return rejectWithValue({ message: "Missing group id" });
    }
    try {
      const response = await axios.patch(
        `${ROOT_URL}/group/${groupId}/ticket-from-email`,
        { ticket_from_email: ticket_from_email.trim() },
        authHeaders(getState),
      );
      return { groupId, payload: response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message, groupId },
      );
    }
  },
);

const initialState = {
  groupId: null,
  ticket_from_email: "",
  loading: false,
  saving: false,
  error: null,
  loaded: false,
};

const groupTicketFromEmailApi = createSlice({
  name: "groupTicketFromEmail",
  initialState,
  reducers: {
    clearGroupTicketFromEmail: (state) => {
      state.groupId = null;
      state.ticket_from_email = "";
      state.error = null;
      state.loaded = false;
    },
    clearGroupTicketFromEmailError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGroupTicketFromEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.loaded = false;
      })
      .addCase(fetchGroupTicketFromEmail.fulfilled, (state, action) => {
        state.loading = false;
        state.loaded = true;
        state.groupId = action.payload.groupId;
        state.ticket_from_email = ticketFromEmailFromPayload(action.payload.payload);
      })
      .addCase(fetchGroupTicketFromEmail.rejected, (state, action) => {
        state.loading = false;
        state.loaded = true;
        state.groupId = action.payload?.groupId ?? action.meta?.arg?.groupId ?? null;
        if (action.payload?.notFound) {
          state.ticket_from_email = "";
          state.error = null;
        } else {
          state.error = action.payload;
        }
      })
      .addCase(patchGroupTicketFromEmail.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(patchGroupTicketFromEmail.fulfilled, (state, action) => {
        state.saving = false;
        state.groupId = action.payload.groupId;
        const fromApi = ticketFromEmailFromPayload(action.payload.payload);
        const fromArg = action.meta?.arg?.ticket_from_email;
        state.ticket_from_email = String(fromApi || fromArg || "").trim();
      })
      .addCase(patchGroupTicketFromEmail.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      });
  },
});

export const { clearGroupTicketFromEmail, clearGroupTicketFromEmailError } =
  groupTicketFromEmailApi.actions;

export default groupTicketFromEmailApi.reducer;
